import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Image as ImageIcon, Globe, Lock, Calendar, User, ChevronDown } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const GalleriesPage = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pastEvents, setPastEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  
  const [newGallery, setNewGallery] = useState({
    title: "",
    description: "",
    is_public: false,
    customer_name: "",
    customer_email: "",
    event_date: "",
    booking_request_id: ""
  });

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API}/galleries`, { withCredentials: true });
      setGalleries(response.data);
    } catch (error) {
      toast.error("Failed to fetch galleries");
    } finally {
      setLoading(false);
    }
  };

  const fetchPastEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await axios.get(`${API}/galleries/past-events`, { withCredentials: true });
      setPastEvents(response.data);
    } catch (error) {
      console.error("Failed to fetch past events");
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
    fetchPastEvents();
  };

  const handleSelectPastEvent = (eventId) => {
    if (!eventId || eventId === "none") {
      // Clear auto-fill if "none" selected
      setNewGallery(prev => ({
        ...prev,
        booking_request_id: "",
        title: "",
        customer_name: "",
        customer_email: "",
        event_date: ""
      }));
      return;
    }

    const event = pastEvents.find(e => e.request_id === eventId);
    if (event) {
      // Format event name from event details
      let eventTitle = "";
      if (event.event_type === "sports" && event.team_name) {
        eventTitle = `${event.team_name} - ${event.venue_name || "Sports Event"}`;
      } else if (event.event_type && event.venue_name) {
        eventTitle = `${event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)} at ${event.venue_name}`;
      } else {
        eventTitle = `${event.customer_name}'s ${event.event_type || "Event"}`;
      }

      // Format date for input
      let formattedDate = "";
      if (event.event_date) {
        const date = new Date(event.event_date);
        formattedDate = date.toISOString().split("T")[0];
      }

      setNewGallery(prev => ({
        ...prev,
        booking_request_id: event.request_id,
        title: eventTitle,
        customer_name: event.customer_name || "",
        customer_email: event.customer_email || "",
        event_date: formattedDate
      }));
    }
  };

  const handleCreateGallery = async () => {
    if (!newGallery.title) {
      toast.error("Please enter a title");
      return;
    }

    try {
      await axios.post(`${API}/galleries`, newGallery, { withCredentials: true });
      toast.success("Gallery created!");
      setDialogOpen(false);
      setNewGallery({
        title: "",
        description: "",
        is_public: false,
        customer_name: "",
        customer_email: "",
        event_date: "",
        booking_request_id: ""
      });
      fetchGalleries();
    } catch (error) {
      toast.error("Failed to create gallery");
    }
  };

  const formatEventLabel = (event) => {
    const date = event.event_date ? new Date(event.event_date).toLocaleDateString() : "";
    return `${event.customer_name} - ${event.event_type || "Event"}${date ? ` (${date})` : ""}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="galleries-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Galleries</h1>
          <p className="text-muted-foreground">Manage your photo galleries</p>
        </div>
        
        <Button 
          className="btn-primary rounded-sm"
          onClick={handleOpenDialog}
          data-testid="create-gallery-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Gallery
        </Button>
      </div>

      {galleries.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg mb-2">No galleries yet</p>
            <p className="text-muted-foreground mb-4">Create a gallery to start uploading photos</p>
            <Button onClick={handleOpenDialog}>
              Create Your First Gallery
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleries.map((gallery, index) => (
            <motion.div
              key={gallery.gallery_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={`/dashboard/galleries/${gallery.gallery_id}`}>
                <Card className="bg-card border-border card-hover overflow-hidden group">
                  {/* Cover Image or Placeholder */}
                  <div className="aspect-[4/3] relative bg-muted">
                    {gallery.cover_image_filename ? (
                      <img
                        src={`${API}/media/${gallery.gallery_id}/${gallery.cover_image_filename}${gallery.is_public ? '' : `?token=${gallery.magic_link_token}`}`}
                        alt={gallery.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center">
                        <p className="text-2xl font-serif text-muted-foreground mb-2 line-clamp-2">
                          {gallery.title}
                        </p>
                        {gallery.event_date && (
                          <p className="text-lg text-muted-foreground/70">
                            {new Date(gallery.event_date).toLocaleDateString('en-US', { 
                              month: 'long', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-sm uppercase tracking-widest">View Gallery</span>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-serif text-lg truncate">{gallery.title}</h3>
                      {gallery.is_public ? (
                        <Badge variant="outline" className="shrink-0 text-green-400 border-green-400/30">
                          <Globe className="w-3 h-3 mr-1" />
                          Public
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="shrink-0 text-muted-foreground">
                          <Lock className="w-3 h-3 mr-1" />
                          Private
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {gallery.asset_count || 0} photos
                      {gallery.is_delivered && " • Delivered"}
                    </p>
                    {gallery.customer_name && (
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {gallery.customer_name}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Gallery Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Create New Gallery</DialogTitle>
            <DialogDescription>
              Create a gallery to organize and share your photos
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Past Events Selection */}
            {pastEvents.length > 0 && (
              <div className="p-4 bg-muted/30 rounded-lg border border-border">
                <Label className="mb-2 block text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Select from Past Booking
                </Label>
                <Select
                  onValueChange={handleSelectPastEvent}
                  value={newGallery.booking_request_id || "none"}
                >
                  <SelectTrigger data-testid="past-event-select">
                    <SelectValue placeholder="Choose a past event to auto-fill details" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Start fresh —</SelectItem>
                    {pastEvents.map((event) => (
                      <SelectItem key={event.request_id} value={event.request_id}>
                        {formatEventLabel(event)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Selecting an event will auto-fill the details below
                </p>
              </div>
            )}

            <div>
              <Label className="mb-2 block">Gallery Title *</Label>
              <Input
                value={newGallery.title}
                onChange={(e) => setNewGallery(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Smith Wedding - June 2024"
                data-testid="gallery-title"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Description</Label>
              <Textarea
                value={newGallery.description}
                onChange={(e) => setNewGallery(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description..."
                rows={3}
                data-testid="gallery-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Customer Name</Label>
                <Input
                  value={newGallery.customer_name}
                  onChange={(e) => setNewGallery(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="John Smith"
                  data-testid="gallery-customer-name"
                />
              </div>
              <div>
                <Label className="mb-2 block">Event Date</Label>
                <Input
                  type="date"
                  value={newGallery.event_date}
                  onChange={(e) => setNewGallery(prev => ({ ...prev, event_date: e.target.value }))}
                  data-testid="gallery-date"
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Customer Email (for delivery)</Label>
              <Input
                type="email"
                value={newGallery.customer_email}
                onChange={(e) => setNewGallery(prev => ({ ...prev, customer_email: e.target.value }))}
                placeholder="customer@example.com"
                data-testid="gallery-email"
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-sm">
              <div>
                <Label>Public Gallery (for sales)</Label>
                <p className="text-sm text-muted-foreground">
                  Public galleries allow customers to browse and purchase photos
                </p>
              </div>
              <Switch
                checked={newGallery.is_public}
                onCheckedChange={(checked) => setNewGallery(prev => ({ ...prev, is_public: checked }))}
                data-testid="gallery-public"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateGallery} data-testid="create-gallery-submit">
              Create Gallery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleriesPage;
